// src/templateManager/components/builders/SecurityCategoryBuilder.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { useFormContext, Controller } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Group,
  Badge,
  TagsInput,
  Accordion,
  Textarea
} from '@mantine/core';

/**
 * SecurityCategoryBuilder - Edits security category schema
 * Handles complex nested structures (options, subcategories, attributes)
 */
function SecurityCategoryBuilder({ categoryIndex }) {
  const { control, watch } = useFormContext();

  const category = watch(`taxonomy.securityTaxonomy.security.${categoryIndex}`) || {};
  const schema = category.schema || {};

  // Render based on schema type
  const renderSchema = () => {
    // Type 1: Simple multiselect with options and attributes
    if (schema.type === 'multiselect' && schema.options) {
      return (
        <Stack gap="md">
          {/* Main options */}
          <Controller
            name={`taxonomy.securityTaxonomy.security.${categoryIndex}.schema.options`}
            control={control}
            render={({ field }) => (
              <TagsInput
                {...field}
                label="Threat Agent Types"
                placeholder="Type option and press Enter"
                description="Types of threat agents to assess"
                splitChars={[',']}
              />
            )}
          />

          {/* Attributes (nested categories) */}
          {schema.attributes && (
            <Card withBorder p="md" bg="gray.0">
              <Stack gap="md">
                <Text size="sm" fw={600}>Threat Attributes</Text>
                {Object.entries(schema.attributes).map(([attrKey, attrValue]) => (
                  <Controller
                    key={attrKey}
                    name={`taxonomy.securityTaxonomy.security.${categoryIndex}.schema.attributes.${attrKey}.options`}
                    control={control}
                    render={({ field }) => (
                      <TagsInput
                        {...field}
                        label={attrKey}
                        placeholder="Type option and press Enter"
                        splitChars={[',']}
                      />
                    )}
                  />
                ))}
              </Stack>
            </Card>
          )}
        </Stack>
      );
    }

    // Type 2: Category with subcategories (Attack Surfaces)
    if (schema.type === 'category' && schema.subcategories) {
      return (
        <Stack gap="md">
          {schema.description && (
            <Controller
              name={`taxonomy.securityTaxonomy.security.${categoryIndex}.schema.description`}
              control={control}
              render={({ field }) => (
                <Textarea
                  {...field}
                  label="Category Description"
                  placeholder="Describe this category..."
                  minRows={2}
                />
              )}
            />
          )}

          <Accordion>
            {schema.subcategories.map((subcategory, subIndex) => (
              <Accordion.Item key={subIndex} value={subIndex.toString()}>
                <Accordion.Control>
                  <Group>
                    <Text fw={500}>{subcategory.name}</Text>
                    <Badge size="sm" variant="light">
                      {subcategory.options?.length || 0} options
                    </Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack gap="md">
                    {/* Subcategory name */}
                    <Controller
                      name={`taxonomy.securityTaxonomy.security.${categoryIndex}.schema.subcategories.${subIndex}.name`}
                      control={control}
                      render={({ field }) => (
                        <Text size="sm" fw={500}>
                          {field.value}
                        </Text>
                      )}
                    />

                    {/* Subcategory options */}
                    <Controller
                      name={`taxonomy.securityTaxonomy.security.${categoryIndex}.schema.subcategories.${subIndex}.options`}
                      control={control}
                      render={({ field }) => (
                        <TagsInput
                          {...field}
                          label="Attack Vectors"
                          placeholder="Type option and press Enter"
                          description={`Attack vectors for ${subcategory.name}`}
                          splitChars={[',']}
                        />
                      )}
                    />
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            ))}
          </Accordion>
        </Stack>
      );
    }

    // Fallback: Display raw structure
    return (
      <Card withBorder p="md">
        <Text size="sm" c="dimmed">
          Schema type: {schema.type || 'undefined'}
        </Text>
        <Text size="xs" c="dimmed" mt="xs">
          {JSON.stringify(schema, null, 2)}
        </Text>
      </Card>
    );
  };

  return (
    <Stack gap="md" mt="md">
      {/* Category description */}
      <Controller
        name={`taxonomy.securityTaxonomy.security.${categoryIndex}.description`}
        control={control}
        render={({ field }) => (
          <Textarea
            {...field}
            label="Category Description"
            placeholder="Describe this security category..."
            minRows={2}
          />
        )}
      />

      {/* Schema editor */}
      {renderSchema()}
    </Stack>
  );
}

SecurityCategoryBuilder.propTypes = {
  categoryIndex: PropTypes.number.isRequired
};

export default SecurityCategoryBuilder;
